import React, { useState, useRef, useEffect, memo, Suspense } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import Control from '../store/control';
import Param from '../store/param';
import { ToCoord3D } from '../shape/Intersect';
import { GetMaterial } from '../fileIO/fileLoader';
import { SystemUpdate, ThreeDRotation } from '@material-ui/icons';
import { drawIntersectionPoints, getContours} from './utils';

const MLeg = memo(({ 
  meshLeg, 
  clickable, 
  showWire, 
  showTransparent, 
  side, 
  loadParam, 
  modifyBackWall,
  fileExt, 
  flexAngle, 
  abductAngle, 
  callback0, 
  callback1, 
  opacityLeg=1.0
}) => {
  const ref        = useRef();

  const [prevTranslationX, setPrevTranslationX] = useState(0);
  const [prevTranslationY, setPrevTranslationY] = useState(0);
  const [prevTranslationZ, setPrevTranslationZ] = useState(0);
  
  const [prevTranslationGlobalX, setPrevTranslationGlobalX] = useState(0);
  const [prevTranslationGlobalY, setPrevTranslationGlobalY] = useState(0);
  const [prevTranslationGlobalZ, setPrevTranslationGlobalZ] = useState(0);
  
  const [doneInitialize, setDoneInitialize] = useState(false);

  // const rotationX     = Param.Get('setup')['rotationX'];
  // const rotationY     = Param.Get('setup')['rotationY'];
  // const rotationZ     = Param.Get('setup')['rotationZ'];
  const translationX     = Param.Get('setup')['translationX'];
  const translationY     = Param.Get('setup')['translationY'];
  const translationZ     = Param.Get('setup')['translationZ'];
  const globalTranslationX     = Param.Get('setup')['globalTranslationX'];
  const globalTranslationY     = Param.Get('setup')['globalTranslationY'];
  const globalTranslationZ     = Param.Get('setup')['globalTranslationZ'];

  const activeStep    = Param.Get('activeStep');
  const align         = Param.Get('align');
  const setParam      = Param.Get('setParam');

  // const xAngle        = Control.Get("xAngle");
  // const yAngle        = Control.Get("yAngle");
  // const zAngle        = Control.Get("zAngle");
  const redrawAngle   = Control.Get('redrawAngle');
  const setControl    = Control.Get('setControl');
  const toCenter      = Control.Get('toCenter');
  const toCenterButton      = Control.Get('toCenterButton');
  const toModifyCenter  = Control.Get('toModifyCenter');
  const toModifyYheight = Control.Get('toModifyYheight');
  const toModifyX = Control.Get('toModifyX');
  const toModifyZ = Control.Get('toModifyZ');
  const origin        = Control.Get('origin');


  const isCenterX   = (activeStep == 1) && (align.step == 2);

  const [objects, setObjects] = useState([]);
  const [subObjects, setSubObjects] = useState([]);
  const [cAngle, setCAngle]   = useState([align.rotationX, align.rotationY, align.rotationZ]);
  const [didModifyCenter, setDidModifyCenter] = useState(0); // TODO: make it much smarter
  const { camera, mouse, scene } = useThree();
  var key = 0;

  // when uploading param data, we will use aligned leg file. So we need to substruct the translation value of the aligned leg file.
  useEffect(() => {
    if (loadParam) {
      setPrevTranslationGlobalX(translationX);
      setPrevTranslationGlobalY(translationY);
      setPrevTranslationGlobalZ(translationZ);
    }
  }, [])

  /**
   * Sets the origin of the given mesh to its center point.
   *
   * @function
   * @param {THREE.Mesh} mesh - The mesh to be centered.
   */
  function setOriginToCenter(mesh, center=null) {
    if (center == null) center = getCenterPoint(mesh);

    setParam(s => { s.setup.translationX = -center.x + prevTranslationX });
    setParam(s => { s.setup.translationY = -center.y + prevTranslationY });
    setParam(s => { s.setup.translationZ = -center.z + prevTranslationZ });

    setPrevTranslationX(-center.x + prevTranslationX);
    setPrevTranslationY(-center.y + prevTranslationY);
    setPrevTranslationZ(-center.z + prevTranslationZ);

    // save how much the mesh is moved
    callback0(new THREE.Vector3(-center.x + prevTranslationX, -center.y + prevTranslationY, -center.z + prevTranslationZ));
  }

  /**
   * Sets the origin of the given mesh to its center point.
   * 
   * @function
   * @param {THREE.Mesh} mesh - The mesh to be centered.
   * @returns {THREE.Vector3} The center point of the given mesh.
   * 
   */
  function setModifyCenter(mesh) {
    if (!didModifyCenter) {
      mesh.geometry.attributes.position.needsUpdate = true;
      for (var i = 0; i < mesh.geometry.attributes.position.array.length; i++) {
        if (i%3 == 0) {
          mesh.geometry.attributes.position.array[i] -= origin.x;
        } else if (i%3 == 1) {
          mesh.geometry.attributes.position.array[i] -= origin.z;
        } else if (i%3 == 2) {
          mesh.geometry.attributes.position.array[i] -= origin.y;
        }
      }
      setDidModifyCenter(true); // TODO: it is effective only at once, so if user move back to the previous step, not effective.
    }
    setControl(s => {s.meshLeg = mesh});
  }

  /**
   * Gets the center point of the given mesh.
   *  
   * @function
   * @param {THREE.Mesh} mesh - The mesh to get the center point of.
   * @returns {THREE.Vector3} The center point of the given mesh.
   */
  function getCenterPoint(mesh) {
    // var geometry = mesh.geometry;
    // geometry.computeBoundingBox();

    // var center = new THREE.Vector3();
    // geometry.boundingBox.getCenter(center);

    //mesh.localToWorld( center );

    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);

    return center;
  }

  /**
   * Gets the click point of the given mesh.
   * 
   * @function
   */
  const onClick = () => {
    var pt = ToCoord3D(meshLeg, camera, mouse);
    if(pt){
      console.log('Leg click (' + mouse.x.toFixed(2) + ", " + mouse.y.toFixed(2) + ") --> ("+ pt.x.toFixed(2) + ", " + pt.y.toFixed(2) + ", " + pt.z.toFixed(2) + ")");
      setControl(s => {s.click = pt});
    }
  };

  // initialize side of leg
  useEffect(() => {
    // when loading param data, we don't need to mirror it, because it is already mirrored.
    if (!loadParam && meshLeg) {
      if (side == 1) {
        meshLeg.scale.x = -1;
      }
    }
  }, [])

  // mirror side of leg
  useEffect(() => {
    if (ref.current) {
      if (side == 0) { // left
        ref.current.scale.x = 1;
      } else { // right
        ref.current.scale.x = -1;
      }
    }
  }, [side])

  // translation
  useEffect(() => {
    var refMesh = ref.current;

    if (refMesh) {
      if (loadParam == false || (loadParam == true && doneInitialize)) {
        // refMesh.position.x = translationX + globalTranslationX;
        // refMesh.position.z = translationZ + globalTranslationY;
        // refMesh.position.y = translationY + globalTranslationZ;

        

        // To ratate global coordinate, rotate the center of mesh
        refMesh.geometry.attributes.position.needsUpdate = true;
        for (var i = 0; i < refMesh.geometry.attributes.position.array.length; i++) {
          if (i%3 == 0) {
            refMesh.geometry.attributes.position.array[i] += translationX + globalTranslationX - prevTranslationGlobalX;
          } else if (i%3 == 1) {
            refMesh.geometry.attributes.position.array[i] += translationY + globalTranslationY - prevTranslationGlobalY;
          } else if (i%3 == 2) {
            refMesh.geometry.attributes.position.array[i] += translationZ + globalTranslationZ - prevTranslationGlobalZ;
          }

          setPrevTranslationGlobalX(translationX + globalTranslationX);
          setPrevTranslationGlobalY(translationY + globalTranslationY);
          setPrevTranslationGlobalZ(translationZ + globalTranslationZ);
        }
      } else {
        setDoneInitialize(true);
      }
      setControl(s => {s.meshLeg = refMesh});
      setControl(s => {s.redrawBox += 1})
    }
  }, [translationX, translationY, translationZ, globalTranslationX, globalTranslationY, globalTranslationZ])

  // rotation
  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh) {
      // refMesh.rotation.x = Math.PI / 180 * align.rotationX;
      // refMesh.rotation.y = Math.PI / 180 * align.rotationY;
      // refMesh.rotation.z = Math.PI / 180 * align.rotationZ;

      // Rotation x
      if (redrawAngle[0]) {
        const rotationMatrixX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), Math.PI / 180 * align.rotationX);
        // apply rotation
        refMesh.geometry.applyMatrix4(rotationMatrixX);
      }

      // Rotation y
      if (redrawAngle[1]) {
        const rotationMatrixY = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), Math.PI / 180 * align.rotationY);
        // apply rotation
        refMesh.geometry.applyMatrix4(rotationMatrixY);
      }

      // Rotation z
      if (redrawAngle[2]) {
        const rotationMatrixZ = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), Math.PI / 180 * align.rotationZ);
        // apply rotation
        refMesh.geometry.applyMatrix4(rotationMatrixZ);
      }

      setControl(s => {s.redrawBox += 1});
      setControl(s => {s.meshLeg = refMesh});
      
      callback1([align.rotationX, align.rotationY, align.rotationZ])
    }
  }, [redrawAngle])
  
  // wireframe, transparent
  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh) {
      if (showWire) {
        refMesh.material = GetMaterial('WIREFRAME');
      } else if (showTransparent) {
        //refMesh.material = GetMaterial('TRANSPARENT');
        refMesh.material.transparent = true;
        refMesh.material.opacity = 0.1;
      } else {
        if(fileExt == 0){ // PLY
          refMesh.material = GetMaterial('VERTEX');
        }else{            // STL
          refMesh.material = GetMaterial('NORMAL');
        }
        refMesh.material.transparent = true;
        refMesh.material.opacity = opacityLeg;
      }
    }
  }, [showWire, showTransparent])

  // opacity
  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh) {
      refMesh.material.transparent = true;
      refMesh.material.opacity = opacityLeg;
    }
  }, [opacityLeg])

  
  useEffect(() => {
    var refMesh = ref.current;
    const center = ( toCenter ) ? align.legCenter : null;

    if ( refMesh && ( toCenter || toCenterButton ) ) {
      if (loadParam == false) {
        setOriginToCenter(refMesh, center);
        setControl(s => {s.meshLeg = refMesh});
      }
    }
    setControl(s => {s.toCenter = false});
    setControl(s => {s.toCenterButton = false});
    setControl(s => {s.redrawBox += 1})
  }, [toCenter, toCenterButton]) // toCenter is set to true when workflow is changed to the Alignment


  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyCenter ) {
      setModifyCenter(refMesh);
      setControl(s => {s.toModifyCenter = false});
    }
  }, [toModifyCenter])

  // set height level
  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyYheight ) {
      setParam(s => {s.setup.translationY -= origin.y});
      setControl(s => {s.toModifyYheight = false});
    }
  }, [toModifyYheight])

  // set origin X and move the mesh
  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyX ) {
      setParam(s => {s.setup.translationX -= origin.x});
      setParam(s => {s.align.vpt_ptbI.x -= origin.x});
      setParam(s => {s.align.vpt_ptbO.x -= origin.x});
      setControl(s => {s.toModifyX = false});
    }
  }, [toModifyX])

  // set origin Z and move the mesh
  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyZ ) {
      setParam(s => {s.setup.translationZ -= origin.z});
      setControl(s => {s.toModifyZ = false});
    }
  }, [toModifyZ])

  // rotate from the origin as temporary, but it could be needed to be changed to rotate from the center of the mesh
  useEffect(() => {
    var refMesh = ref.current;  
    if (refMesh) {
      console.log(refMesh.rotation.x) 
      refMesh.rotation.x = -Math.PI / 180 * flexAngle;
      // refMesh.rotation.x -= Math.PI / 2;

      console.log("rotate X", flexAngle);
    }
  }, [flexAngle]);

  // rotate from the origin as temporary, but it could be needed to be changed to rotate from the center of the mesh
  useEffect(() => {
    var refMesh = ref.current;  
    if (refMesh) {
      refMesh.rotation.z = Math.PI / 180 * align.rotationZ + Math.PI / 180 * abductAngle;
      console.log("rotate Y", abductAngle);
    }
  }, [abductAngle]);

  // Get contour for stump length
  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh && isCenterX) {
      var plane = new THREE.Plane(new THREE.Vector3(1,0,0), -origin.x);
      var vertices = drawIntersectionPoints(refMesh, plane, origin.x);
      
      try {
        var contours = getContours(vertices, [], true);
        setControl(s => {s.projectPoints = contours[0]});
      } catch (error) {
        console.log(error);
      }
    }
  }, [origin]);

  // Set leg
  useEffect(() => {
    console.log('clickable = ' + clickable);

    //meshLeg.rotation.x = -Math.PI / 2; // opengl is different coordinates, so rotate 90 degree
    console.log(objects.length)
    if ( meshLeg != null ) {
      if (objects.length >= 1) {
        setObjects(objects.pop()); // remove current object
      }

      if (clickable) {
        setObjects([<primitive key='leg' ref={ref} object={meshLeg} onClick={onClick} />])
      } else{ 
        setObjects([<primitive key='leg' ref={ref} object={meshLeg} />]);
      }
      console.log("Leg verices = " + meshLeg.geometry.attributes.position.count);
    }
  }, [meshLeg, clickable])

  // Modify back wall
  useEffect(() => {
    //if (modifyBackWall) {
    if (false) {
      console.log('Modify Back Wall')
      setParam(s => {s.marking.modifyBackWall = false});

      if (subObjects.length >= 1) {
        setSubObjects(subObjects.pop()); // remove current object
        setSubObjects(subObjects.pop()); // remove current object
      }
      
      // add plane buffer geometry to the subObjects
      subObjects.push(
        <mesh key={key} position-z={-20}>
          <planeBufferGeometry attach='geometry' args={[200, 200]} />
          <meshStandardMaterial attach='material' transparent opacity={0.5} side={THREE.DoubleSide} color="orange" />
        </mesh>
      )
      key += 1;

      // rotate the mesh by 90 degree around x axis
      subObjects.push(
        <mesh key={key} rotation-x={-Math.PI / 2} position-y={20}>
          <planeBufferGeometry attach='geometry' args={[200, 200]} />
          <meshStandardMaterial attach='material' transparent opacity={0.5} side={THREE.DoubleSide} color="orange" />
        </mesh>
      )
      key += 1;

      setSubObjects(subObjects)


      var refMesh = ref.current;  
      refMesh.geometry.computeBoundingBox();
      const limitZ = refMesh.geometry.boundingBox.min.z*1.3;
      const translateZ = -100;
      if (refMesh) {
        // Translate vertices of the refmesh. Target vertices are y > 20 and z < -20.
        // Translate value is 100
        var vertices = refMesh.geometry.attributes.position.array;
        for (var i = 0; i < vertices.length; i+=3) {
          if (vertices[i+1] > 20 && vertices[i+2] < -20) {
            if (vertices[i+2]+translateZ > limitZ) {
              vertices[i+2] += translateZ;
            } else {
              vertices[i+2] = limitZ;
            }
          }
        }
        refMesh.geometry.attributes.position.needsUpdate = true;
      }
      
    }
  }, [modifyBackWall])
  
  const isObject = (objects.length != 0); 
  const isSubObject = (subObjects.length != 0);

  return (
    <>
      { isObject ? objects : null }
      { isSubObject ? subObjects : null }
    </>
  )
});

export default MLeg
